import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createRewardBody } from '../../../services/api/apiPayloads';
import {
    getRewardList,
    openRewardEditor,
    renderRewardEditor,
    updateReward,
} from '../../../store/actions/rewardActions';
import Reward from './Reward';

const RewardList = () => {
    const dispatch = useDispatch();
    const { rewardItems, rewardEditor } = useSelector((state) => state.reward);
    const { hasReadOnly } = useSelector((state) => state?.auth?.user);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [showExpired, setShowExpired] = useState(false);

    const handleShowExpiredChange = (event) => {
        setShowExpired(event.target.checked);
        setPage(0);
    };

    const filteredRows = useMemo(() => {
        return showExpired
            ? rewardItems
            : rewardItems.filter((row) => row.status !== 'EXPIRED');
    }, [rewardItems, showExpired]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredRows.slice(start, start + rowsPerPage);
    }, [filteredRows, page, rowsPerPage]);

    useEffect(() => {
        dispatch(getRewardList());
    }, [rewardEditor, dispatch]);

    const handleEdit = (id) => {
        dispatch(renderRewardEditor(id));
    };

    const handleExpire = (id) => {
        const reward = rewardItems.find((item) => item.loyaltyRewardId === id);
        const {
            promotionType,
            name,
            description,
            deductedPoint,
            interval,
            winRate,
            winLimit,
            winLimitPerUser,
            loyaltyPrizeItems,
            startDate,
            endDate,
        } = reward;

        const prizeItems = loyaltyPrizeItems.map(
            (item) => item.loyaltyPrizeItemId
        );
        const rewardExpireBody = createRewardBody(
            promotionType,
            name,
            description,
            deductedPoint,
            winRate,
            winLimit,
            winLimitPerUser,
            interval,
            startDate,
            endDate,
            prizeItems,
            'EXPIRED'
        );
        dispatch(updateReward(id, rewardExpireBody));
    };
    return (
        <>
            {rewardEditor.open ? (
                <Reward />
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Promotion Type</TableCell>
                                <TableCell>Reward Id</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell
                                    colSpan={2}
                                    sx={{ textAlign: 'center' }}
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.map((reward, id) => (
                                <TableRow key={id}>
                                    <TableCell>
                                        {reward.promotionType === 'MGM'
                                            ? 'MGM DEFAULT'
                                            : reward.promotionType.replaceAll(
                                                  '_',
                                                  ' '
                                              )}
                                    </TableCell>
                                    <TableCell>{reward.rewardId}</TableCell>
                                    <TableCell>{reward.name}</TableCell>
                                    <TableCell>{reward.description}</TableCell>
                                    <TableCell>{reward.status}</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Button
                                            variant="contained"
                                            onClick={() =>
                                                handleEdit(
                                                    reward.loyaltyRewardId
                                                )
                                            }
                                        >
                                            {!hasReadOnly && reward.status === 'DRAFT'
                                                ? 'Edit'
                                                : 'View'}
                                        </Button>
                                    </TableCell>
                                    {!hasReadOnly && (
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() =>
                                                    handleExpire(
                                                        reward.loyaltyRewardId
                                                    )
                                                }
                                                disabled={
                                                    reward.status === 'EXPIRED'
                                                }
                                            >
                                                Expire
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Stack direction="row" spacing="2">
                            {!hasReadOnly && (
                                <Button
                                    variant="contained"
                                    sx={{ margin: '1rem 0' }}
                                    onClick={() =>
                                        dispatch(openRewardEditor(undefined))
                                    }
                                >
                                    Add New Reward
                                </Button>
                            )}

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showExpired}
                                        onChange={handleShowExpiredChange}
                                    />
                                }
                                label="Show Expired"
                            />
                        </Stack>
                        <TablePagination
                            count={filteredRows.length}
                            page={page}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(event, newPage) => setPage(newPage)}
                            onRowsPerPageChange={(event) => {
                                setRowsPerPage(
                                    parseInt(event.target.value, 10)
                                );
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{
                                borderBottom: 'none',
                            }}
                        />
                    </Box>
                </TableContainer>
            )}{' '}
        </>
    );
};

export default RewardList;
